import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAIStore } from '../../stores/aiStore';
import type { AIBackendType, AIChatMessage, AIModelOption } from '../../services/ai/types';

interface AIChatPanelProps {
  palette: {
    panelBg: string;
    panelBorder: string;
    panelText: string;
    panelSubText: string;
  };
  glass: React.CSSProperties;
}

const BACKEND_LABELS: Record<AIBackendType, string> = {
  webllm: 'WebLLM (Browser)',
  ollama: 'Ollama (Local)',
  'gemini-nano': 'Gemini Nano',
  none: 'None',
};

const STATUS_DOT: Record<string, string> = {
  ready: '#22c55e',
  downloading: '#f59e0b',
  downloadable: '#3b82f6',
  unavailable: '#64748b',
  error: '#ef4444',
};

export function AIChatPanel({ palette, glass }: AIChatPanelProps) {
  const {
    isPanelOpen, setPanelOpen,
    backendType, backendStatus, downloadProgress,
    availableBackends, availableModels, selectedModel,
    messages, isProcessing,
    probeBackends, selectBackend, setSelectedModel, initializeBackend,
    sendMessage, clearChat,
  } = useAIStore();

  const [input, setInput] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasProbed = useRef(false);
  useEffect(() => {
    if (isPanelOpen && !hasProbed.current) {
      hasProbed.current = true;
      probeBackends().then(() => {
        const { backend } = useAIStore.getState();
        if (!backend) selectBackend();
      });
    }
  }, [isPanelOpen, probeBackends, selectBackend]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isProcessing]);

  useEffect(() => {
    if (isPanelOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isPanelOpen]);

  const handleLoadModel = useCallback(async () => {
    setInitError(null);
    try {
      await initializeBackend();
    } catch (err) {
      setInitError(err instanceof Error ? err.message : String(err));
    }
  }, [initializeBackend]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isProcessing) return;
    setInput('');
    await sendMessage(text);
  }, [input, isProcessing, sendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    e.stopPropagation();
  }, [handleSend]);

  if (!isPanelOpen) return null;

  const canChat = backendStatus === 'ready' && !isProcessing;
  const needsLoad = backendStatus === 'downloadable' || backendStatus === 'error';

  return (
    <div
      className="fixed right-4 bottom-4 z-[200] flex flex-col rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden"
      style={{ ...glass, width: 380, height: 520, maxHeight: 'calc(100vh - 80px)' }}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b shrink-0"
        style={{ borderColor: palette.panelBorder }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#a78bfa' }}>
            <path d="M12 2a8 8 0 0 0-8 8c0 3.4 2.1 6.3 5 7.5V20h6v-2.5c2.9-1.2 5-4.1 5-7.5a8 8 0 0 0-8-8z" />
            <path d="M10 22h4" />
          </svg>
          <span className="text-sm font-bold" style={{ color: palette.panelText }}>
            VeloStruct AI
          </span>
          <div
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: STATUS_DOT[backendStatus] ?? '#64748b' }}
            title={`${BACKEND_LABELS[backendType]} — ${backendStatus}`}
          />
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowSettings(s => !s)}
            className="p-1 rounded-md transition-colors hover:opacity-80"
            style={{ color: showSettings ? '#a78bfa' : palette.panelSubText }}
            title="AI Settings"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
            </svg>
          </button>
          <button
            onClick={() => setPanelOpen(false)}
            className="p-1 rounded-md transition-colors hover:opacity-80"
            style={{ color: palette.panelSubText }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {selectedModel && backendStatus === 'ready' && (
        <div className="px-4 py-1 text-[10px] truncate shrink-0" style={{ color: palette.panelSubText, borderBottom: `1px solid ${palette.panelBorder}` }}>
          {BACKEND_LABELS[backendType]} · {shortModelName(selectedModel)}
        </div>
      )}

      {showSettings && (
        <SettingsDropdown
          palette={palette}
          backendType={backendType}
          backendStatus={backendStatus}
          availableBackends={availableBackends}
          availableModels={availableModels}
          selectedModel={selectedModel}
          needsLoad={needsLoad}
          onSelect={async (type) => {
            setInitError(null);
            await selectBackend(type);
          }}
          onSelectModel={async (id) => {
            setInitError(null);
            await setSelectedModel(id);
          }}
          onLoadModel={handleLoadModel}
          onProbe={() => probeBackends()}
        />
      )}

      {backendStatus === 'downloading' && (
        <div className="px-4 py-2 shrink-0" style={{ borderBottom: `1px solid ${palette.panelBorder}` }}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px]" style={{ color: palette.panelSubText }}>Downloading model…</span>
            <span className="text-[10px] font-mono" style={{ color: palette.panelSubText }}>
              {Math.round(downloadProgress * 100)}%
            </span>
          </div>
          <div className="w-full h-1 rounded-full" style={{ background: `${palette.panelBorder}` }}>
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${downloadProgress * 100}%`, background: '#a78bfa' }}
            />
          </div>
        </div>
      )}

      {initError && (
        <div className="px-4 py-2 text-[11px] shrink-0" style={{ color: '#ef4444', borderBottom: `1px solid ${palette.panelBorder}` }}>
          {initError}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {messages.length === 0 && !isProcessing && (
          <EmptyState palette={palette} backendType={backendType} backendStatus={backendStatus} selectedModel={selectedModel} />
        )}
        {messages.map(msg => (
          <ChatBubble key={msg.id} msg={msg} palette={palette} />
        ))}
        {isProcessing && (
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#a78bfa', animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#a78bfa', animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#a78bfa', animationDelay: '300ms' }} />
            </div>
            <span className="text-[11px]" style={{ color: palette.panelSubText }}>Thinking…</span>
          </div>
        )}
      </div>

      <div className="px-3 py-3 border-t shrink-0" style={{ borderColor: palette.panelBorder }}>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={canChat ? 'Describe what to build…' : needsLoad ? 'Load a model in settings first…' : 'Select an AI backend…'}
            disabled={!canChat}
            className="flex-1 bg-transparent border rounded-xl px-3 py-2 text-xs outline-none transition-colors"
            style={{
              color: palette.panelText,
              borderColor: palette.panelBorder,
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || !canChat}
            className="shrink-0 p-2 rounded-xl transition-colors disabled:opacity-30"
            style={{ background: '#a78bfa', color: '#fff' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            className="mt-2 text-[10px] transition-colors hover:opacity-80"
            style={{ color: palette.panelSubText }}
          >
            Clear chat
          </button>
        )}
      </div>
    </div>
  );
}

function shortModelName(id: string): string {
  return id.replace(/-Instruct.*$/i, '').replace(/-q4f16_1-MLC$/i, '').replace(/-MLC$/i, '');
}

function EmptyState({ palette, backendType, backendStatus, selectedModel }: {
  palette: AIChatPanelProps['palette'];
  backendType: AIBackendType;
  backendStatus: string;
  selectedModel: string | null;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-4">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: palette.panelSubText, opacity: 0.5 }}>
        <path d="M12 2a8 8 0 0 0-8 8c0 3.4 2.1 6.3 5 7.5V20h6v-2.5c2.9-1.2 5-4.1 5-7.5a8 8 0 0 0-8-8z" />
        <path d="M10 22h4" />
      </svg>
      <div>
        <div className="text-xs font-medium mb-1" style={{ color: palette.panelText }}>
          Build diagrams with natural language
        </div>
        <div className="text-[10px] leading-relaxed" style={{ color: palette.panelSubText }}>
          Try: "Add a mobile app connected to an API gateway and a database"
        </div>
      </div>
      {backendStatus === 'unavailable' && (
        <div className="text-[10px] px-3 py-1.5 rounded-lg" style={{ color: '#f59e0b', background: 'rgba(245,158,11,0.1)' }}>
          No AI backend available. Install Ollama or use Chrome with WebGPU.
        </div>
      )}
      {backendStatus === 'downloadable' && (
        <div className="text-[10px] px-3 py-1.5 rounded-lg" style={{ color: '#93c5fd', background: 'rgba(59,130,246,0.12)' }}>
          Open the gear icon and click {backendType === 'gemini-nano' ? 'Load Gemini Nano' : 'Download & load'} to choose when the model is fetched.
        </div>
      )}
      {backendStatus === 'ready' && (
        <div className="text-[10px]" style={{ color: palette.panelSubText }}>
          Using {BACKEND_LABELS[backendType]}{selectedModel ? ` (${shortModelName(selectedModel)})` : ''}
        </div>
      )}
    </div>
  );
}

function SettingsDropdown({ palette, backendType, backendStatus, availableBackends, availableModels, selectedModel, needsLoad, onSelect, onSelectModel, onLoadModel, onProbe }: {
  palette: AIChatPanelProps['palette'];
  backendType: AIBackendType;
  backendStatus: string;
  availableBackends: Array<{ type: AIBackendType; status: string }>;
  availableModels: AIModelOption[];
  selectedModel: string | null;
  needsLoad: boolean;
  onSelect: (type: AIBackendType) => void;
  onSelectModel: (id: string) => void;
  onLoadModel: () => void;
  onProbe: () => void;
}) {
  const loadLabel = backendType === 'gemini-nano'
    ? 'Load Gemini Nano'
    : backendType === 'webllm'
      ? 'Download & load model'
      : 'Load model';

  return (
    <div className="px-3 py-3 space-y-2 border-b" style={{ borderColor: palette.panelBorder }}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: palette.panelSubText }}>
          AI Backend
        </span>
        <button
          onClick={onProbe}
          className="text-[10px] hover:opacity-80 transition-colors"
          style={{ color: '#a78bfa' }}
        >
          Refresh
        </button>
      </div>
      {availableBackends.map(b => (
        <button
          key={b.type}
          onClick={() => b.status !== 'unavailable' && onSelect(b.type)}
          disabled={b.status === 'unavailable'}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors text-[11px] ${b.type === backendType ? 'ring-1 ring-purple-400/50' : ''}`}
          style={{
            color: b.status === 'unavailable' ? palette.panelSubText : palette.panelText,
            opacity: b.status === 'unavailable' ? 0.4 : 1,
          }}
        >
          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_DOT[b.status] ?? '#64748b' }} />
          <span className="font-medium">{BACKEND_LABELS[b.type]}</span>
          <span className="text-[9px] ml-auto" style={{ color: palette.panelSubText }}>{b.status}</span>
        </button>
      ))}

      {availableModels.length > 0 && (
        <div className="pt-1 space-y-1">
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: palette.panelSubText }}>
            Model
          </span>
          <select
            value={selectedModel ?? ''}
            onChange={e => onSelectModel(e.target.value)}
            className="w-full rounded-lg px-2 py-1.5 text-[11px] outline-none"
            style={{
              background: 'transparent',
              color: palette.panelText,
              border: `1px solid ${palette.panelBorder}`,
            }}
          >
            {availableModels.map(m => (
              <option key={m.id} value={m.id}>
                {m.label}{m.sizeHint ? ` (${m.sizeHint})` : ''}{m.recommended ? ' · recommended' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {(needsLoad || backendStatus === 'downloading') && backendType !== 'none' && (
        <button
          onClick={onLoadModel}
          disabled={backendStatus === 'downloading'}
          className="w-full mt-1 px-2 py-1.5 rounded-lg text-[11px] font-medium disabled:opacity-50"
          style={{ background: '#a78bfa', color: '#fff' }}
        >
          {backendStatus === 'downloading' ? 'Downloading…' : loadLabel}
        </button>
      )}
    </div>
  );
}

function ChatBubble({ msg, palette }: { msg: AIChatMessage; palette: AIChatPanelProps['palette'] }) {
  const isUser = msg.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed ${isUser ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
        style={{
          background: isUser ? '#a78bfa' : palette.panelBorder,
          color: isUser ? '#fff' : palette.panelText,
        }}
      >
        {msg.content}
        {msg.operations && msg.operations.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {msg.operations.map((op, i) => (
              <div key={i} className="flex items-center gap-1 text-[9px]" style={{ opacity: 0.8 }}>
                <span>{op.success ? '✓' : '✗'}</span>
                <span className="font-mono">{op.method}</span>
                {op.error && <span className="text-red-300"> — {op.error}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
